import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface FeatureFlag {
  feature_key: string;
  enabled: boolean;
  description: string | null;
}

export function useFeatureFlag(featureKey: string) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFlag = async () => {
      try {
        const { data, error } = await supabase
          .from('feature_flags')
          .select('enabled')
          .eq('feature_key', featureKey)
          .single();

        if (error) {
          console.error('Error fetching feature flag:', error);
          setEnabled(false); // Default to disabled on error
        } else {
          setEnabled(data?.enabled ?? false);
        }
      } catch (err) {
        console.error('Error fetching feature flag:', err);
        setEnabled(false);
      } finally {
        setLoading(false);
      }
    };

    fetchFlag();
  }, [featureKey]);

  return { enabled, loading };
}

// Hook to fetch all feature flags (for admin panel)
export function useFeatureFlags() {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchFlags = async () => {
    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('feature_key, enabled, description')
        .order('feature_key');

      if (error) {
        console.error('Error fetching feature flags:', error);
      } else {
        setFlags(data || []);
      }
    } catch (err) {
      console.error('Error fetching feature flags:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFlags();
  }, []);

  const toggleFlag = async (featureKey: string, enabled: boolean) => {
    const { error } = await supabase
      .from('feature_flags')
      .update({ enabled })
      .eq('feature_key', featureKey);

    if (error) {
      console.error('Error updating feature flag:', error);
      throw error;
    }

    // Update local state
    setFlags(prev => prev.map(f => 
      f.feature_key === featureKey ? { ...f, enabled } : f
    ));
  };

  return { flags, loading, toggleFlag, refetch: fetchFlags };
}
